import { APIClient } from '@/services'
import { replyStore, resetReplyState } from '@/stores/reply'

import React from 'react'

type UseCommentSubmitProps = {
  itemId?: number
  value: string
  onSuccess?: () => void
  onError?: (error: string) => void
}

export const useCommentSubmit = ({ itemId, value, onSuccess, onError }: UseCommentSubmitProps) => {
  const [isSending, setIsSending] = React.useState(false)
  const [error, setError] = React.useState('')

  const profile = APIClient.Session.QueryKeys.me.getData()

  const createComment = APIClient.Community.communityCommentsManager.useCreate({
    onListsWithFilters: { item_id: itemId },
  })

  const handleSubmit = React.useCallback(async () => {
    if (!itemId || !value.trim() || isSending) return

    setIsSending(true)

    const parentReply = replyStore.get()?.parent

    const data = {
      text: value,
      item: itemId as number,
      profile,
      created_at: new Date() as any,
      reply_to: parentReply?.id ?? null,
    }

    try {
      if (parentReply) {
        await APIClient.Community.communityCommentsManager.actions.createReply(data, parentReply)
        resetReplyState()
      } else {
        await createComment.create(data)
      }

      APIClient.Community.communityOperations.mutations.updateCommentCount({ postId: itemId, value: 1 })

      logger.log('Comment sent successfully', { itemId, value })
      onSuccess?.()
    } catch (err) {
      logger.error('Error sending comment:', err)

      const errorData = err?.response?.data?.text ||
        err?.data?.text ||
        err?.message ||
        'Failed to send comment'

      setError(errorData)

      onError?.(errorData)
    } finally {
      setIsSending(false)
      resetReplyState()
    }
  }, [value, createComment, itemId, profile, isSending, onSuccess, onError])

  const clearError = React.useCallback(() => {
    setError('')
  }, [])

  return {
    isSending,
    error,
    handleSubmit,
    clearError,
  }
}
